import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Drawer, DrawerProps, message, Select } from 'antd';
import { Book, BookAppendix, Exam, ExerciseFormat, Lecture, Theory } from '../../../../model/response';
import { BookAppendixItemType } from '../../../../model/enum';
import { bookAppendixItemTypeList, getEnumLabel } from '../../../../utils/enumMapper';
import processError from '../../../../utils/processError';
import { findAllLectures } from '../../../../service/lectureService';
import { findAllTheories } from '../../../../service/theoryService';
import { findAllExerciseFormats } from '../../../../service/exerciseFormatService';
import { findAllExams } from '../../../../service/examService';
import { findAllPractices } from '../../../../service/practiceService';
import { addBookAppendixItem } from '../../../../service/bookAppendixItemService';

interface BookAppendixItemFormDrawerProps extends DrawerProps {
  bookAppendix?: BookAppendix;
  book?: Book;
  onBeforeSuccess: () => Promise<void>;
}

function BookAppendixItemFormDrawer({
  bookAppendix,
  book,
  onBeforeSuccess,
  ...restProps
}: BookAppendixItemFormDrawerProps) {
  const [type, setType] = useState<BookAppendixItemType | null>(null);
  const [itemId, setItemId] = useState('');
  const [lectures, setLectures] = useState<Lecture[]>([]);
  const [theories, setTheories] = useState<Theory[]>([]);
  const [exerciseFormats, setExerciseFormats] = useState<ExerciseFormat[]>([]);
  const [exams, setExams] = useState<Exam[]>([]);
  const [practices, setPractices] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchItemData = useCallback(async () => {
    if (!book?.subject.id) {
      return;
    }
    const lectureList = await findAllLectures(book.subject.id);
    setLectures(lectureList);
    const theoryList = await findAllTheories(book.subject.id);
    setTheories(theoryList);
    const exerciseFormatList = await findAllExerciseFormats(book.subject.id);
    setExerciseFormats(exerciseFormatList);
    const examList = await findAllExams(book.subject.id);
    setExams(examList);
    const practiceList = await findAllPractices(book.subject.id);
    setPractices(practiceList);
  }, [book?.subject.id]);

  useEffect(() => {
    fetchItemData().then().catch(processError);
  }, [fetchItemData]);

  const itemPlaceHolder = useMemo(() => {
    if (!type) return '';
    return 'Chọn ' + getEnumLabel(type, bookAppendixItemTypeList) + '...';
  }, [type]);

  const itemLabel = useMemo(() => {
    if (!type) return '';
    return getEnumLabel(type, bookAppendixItemTypeList);
  }, [type]);

  const itemOptions = useMemo(() => {
    if (!type) return [];
    switch (type) {
      case 'EXERCISE_FORMAT':
        const selectedExerciseFormatIds = bookAppendix?.bookAppendixExerciseFormats.map((e) => e.itemId) || [];
        return exerciseFormats
          .filter((e) => !selectedExerciseFormatIds.includes(e.id))
          .map((item) => ({
            value: item.id,
            label: `${item.code} - ${item.name}`,
          }));
      case 'PRACTICE':
        const selectedPracticeIds = bookAppendix?.bookAppendixPractices.map((p) => p.itemId) || [];
        return practices
          .filter((p) => !selectedPracticeIds.includes(p.id))
          .map((item) => ({
            label: item.name,
            value: item.id,
          }));
      case 'EXAM':
        const selectedExamIds = bookAppendix?.bookAppendixExams.map((e) => e.itemId) || [];
        return exams
          .filter((e) => !selectedExamIds.includes(e.id))
          .map((item) => ({
            label: item.name,
            value: item.id,
          }));
      case 'THEORY':
        const selectedTheoryIds = bookAppendix?.bookAppendixTheories.map((e) => e.itemId) || [];
        return theories
          .filter((t) => !selectedTheoryIds.includes(t.id))
          .map((item) => ({
            label: item.name,
            value: item.id,
          }));
      case 'LECTURE':
        const selectedLectureIds = bookAppendix?.bookAppendixLectures.map((e) => e.itemId) || [];
        return lectures
          .filter((l) => !selectedLectureIds.includes(l.id))
          .map((item) => ({
            label: item.name,
            value: item.id,
          }));
    }
  }, [
    type,
    bookAppendix?.bookAppendixExerciseFormats,
    bookAppendix?.bookAppendixPractices,
    bookAppendix?.bookAppendixExams,
    bookAppendix?.bookAppendixTheories,
    bookAppendix?.bookAppendixLectures,
    exerciseFormats,
    practices,
    exams,
    theories,
    lectures,
  ]);

  async function handleSave() {
    if (!bookAppendix) return;
    if (!type) {
      message.error('Vui lòng chọn loại item');
      return;
    }
    if (!itemId) {
      message.error('Vui lòng chọn item');
    }
    try {
      setLoading(true);
      await addBookAppendixItem(bookAppendix.id, itemId, type);
      await onBeforeSuccess();
      message.info('Thêm item thành công');
    } catch (err) {
      processError(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Drawer
      title={'Gán thành phần danh mục sách'}
      placement="right"
      width={400}
      closable={false}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button onClick={(e) => !loading && restProps.onClose && restProps.onClose(e)} className={'mr-2'}>
            Hủy
          </Button>
          <Button type="primary" onClick={handleSave} loading={loading}>
            Lưu
          </Button>
        </div>
      }
      {...restProps}
    >
      <div className={'flex flex-col gap-y-2'}>
        <div>
          <label>
            Tên mục <span className={'text-FF1D30'}>*</span>
          </label>
          <Select
            placeholder="Chọn thể loại"
            value={type}
            onChange={(value) => {
              setType(value);
              setItemId('');
            }}
            style={{ width: '100%' }}
            options={bookAppendixItemTypeList}
          />
        </div>
        <div>
          <label>{itemLabel}</label>
          <Select
            placeholder={itemPlaceHolder}
            value={itemId}
            onChange={setItemId}
            style={{ width: '100%' }}
            options={itemOptions}
            disabled={!type}
          />
        </div>
      </div>
    </Drawer>
  );
}

export default BookAppendixItemFormDrawer;
